import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { CanceledError } from "axios";
import { enqueueSnackbar } from "notistack";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Loader from "_shared/_views/loading/loader";
import { useSession } from "users/__session/session-context";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedFeeSchedule,
} from "work/_shared/masked-fields";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleCategory from "../fee-schedule-category";

const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
  maxWidth: theme.spacing(50),
  overflowY: "auto",
}));

type FeeScheduleCategoryLike =
  | FeeScheduleCategory
  | FeeScheduleCategoryMaskedCurrency;

type FeeScheduleCategoryListProps = {
  categories?: FeeScheduleCategoryLike[];
  disabledCategories?: FeeScheduleCategoryLike[];
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
};

function isMaskedCategory(
  category: FeeScheduleCategoryLike
): category is FeeScheduleCategoryMaskedCurrency {
  return MaskedFeeSchedule.isMasked(category);
}

function isUnmaskedCategory(
  category: FeeScheduleCategoryLike
): category is FeeScheduleCategory {
  return !isMaskedCategory(category);
}

function getCategoryName(category: FeeScheduleCategoryLike): string {
  return category.name?.value ?? "";
}

function dedupeCategoriesByName(
  categories: FeeScheduleCategory[]
): FeeScheduleCategory[] {
  return Array.from(
    new Map(categories.map((category) => [category.name.value, category])).values()
  );
}

export default function FeeScheduleCategoryList({
  categories,
  disabledCategories = [],
  onCategoriesSelected,
}: Readonly<FeeScheduleCategoryListProps>) {
  const session = useSession();

  const [isLoading, setIsLoading] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<FeeScheduleCategory[]>([]);
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());

  const paginationParameters = useMemo(
    () => PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
    []
  );

  const disabledNames = useMemo(
    () =>
      new Set(
        disabledCategories
          .map(getCategoryName)
          .filter((name): name is string => Boolean(name))
      ),
    [disabledCategories]
  );

  const loadCategoriesFromTemplates = useCallback(
    async (abortController: AbortController) => {
      const entityId = session.user?.legalEntityId;
      if (!entityId) {
        setAvailableCategories([]);
        return;
      }

      setIsLoading(true);

      try {
        const filterParameters = new FeeScheduleTemplateFilterParameters({
          entityId,
        });

        const orderParameters = new FeeScheduleTemplateOrderParameters({
          orderBy: FeeScheduleTemplateOrderField.Name,
          orderDir: OrderDirection.Descending,
        });

        const query = new FeeScheduleTemplateQuery({
          filterParameters,
          orderParameters,
          paginationParameters,
        });

        const service = new FeeScheduleTemplateQueryAPIService(session);
        const response = await service.getFeeScheduleTemplatesByQuery(
          query,
          abortController
        );

        const categoriesFromTemplates = response.data.flatMap((template) =>
          (template.categories ?? [])
            .filter((category) => !Masked.isMasked(category))
            .map(
              (category) =>
                FeeScheduleCategory.fromJSON(category) as FeeScheduleCategory
            )
        );

        setAvailableCategories(dedupeCategoriesByName(categoriesFromTemplates));
      } catch (error) {
        if (error instanceof CanceledError) return;

        console.error(error);
        enqueueSnackbar("Failed to load existing fee schedule templates.", {
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [paginationParameters, session]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (categories) {
      const unmaskedCategories = categories.filter(isUnmaskedCategory);
      setAvailableCategories(dedupeCategoriesByName(unmaskedCategories));
      setIsLoading(false);

      return () => {
        abortController.abort();
      };
    }

    loadCategoriesFromTemplates(abortController);

    return () => {
      abortController.abort();
    };
  }, [categories, loadCategoriesFromTemplates]);

  const toggleCategorySelection = useCallback((categoryName: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);

      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }

      return next;
    });
  }, []);

  const handleCategoryClick = useCallback(
    (category: FeeScheduleCategory) => {
      const categoryName = category.name.value;
      if (disabledNames.has(categoryName)) return;

      toggleCategorySelection(categoryName);
    },
    [disabledNames, toggleCategorySelection]
  );

  const handleAddSelectedClicked = useCallback(() => {
    const selectedCategories = availableCategories.filter((category) =>
      selectedNames.has(category.name.value)
    );

    onCategoriesSelected(selectedCategories);
  }, [availableCategories, onCategoriesSelected, selectedNames]);

  const canSubmit = selectedNames.size > 0;

  if (isLoading) {
    return (
      <div>
        <ListContainer>
          <Loader />
        </ListContainer>
      </div>
    );
  }

  return (
    <div>
      <ListContainer>
        {availableCategories.length === 0 ? (
          <Typography>No existing fee schedule categories</Typography>
        ) : (
          availableCategories.map((category) => {
            const categoryName = category.name.value;
            const isDisabled = disabledNames.has(categoryName);
            const isChecked = isDisabled || selectedNames.has(categoryName);

            return (
              <ListItemButton
                key={categoryName}
                alignItems="flex-start"
                disableGutters
                disabled={isDisabled}
                onClick={() => handleCategoryClick(category)}
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ whiteSpace: "pre-line" }}
                  primary={categoryName}
                  secondary={category.feeString}
                />
              </ListItemButton>
            );
          })
        )}
      </ListContainer>

      <Button
        color="primary"
        variant="contained"
        disabled={!canSubmit}
        onClick={handleAddSelectedClicked}
      >
        Add Selected
      </Button>
    </div>
  );
}
